import { useMemo } from 'react';

import { TagModel } from 'models';

import { PreparedFile } from 'features/patient/fragments/PatientFiles.types';
import { aggregateTagCounts, assignCurrentCountsToTree, filterTreeByAllowedIds, getAllParentTagIds } from 'features/patient/services/tagHelperUtils';

export const ALL_FILES_TAG_ID = 0;
export const UNTAGGED_FILES_TAG_ID = -1;
export const CATEGORY_TAG_ID_FOR_UNTRAINED = -1000; // Assuming this is ROOT_CATEGORY_TAG_ID

interface UseTagSidebarDataResult {
  displayableTags: TagModel[];
}

export const useTagSidebarData = (
  preparedFiles: PreparedFile[],
  finalFilteredAndSortedFiles: PreparedFile[],
  mergedHierarchicalTags: TagModel[],
  mergedFlatTags: TagModel[],
  mergedTagNameToIdMap: Map<string, number>,
  isLoadingMergedTags: boolean,
): UseTagSidebarDataResult => {
  const initialTagCounts = useMemo(() => {
    if (isLoadingMergedTags || preparedFiles.length === 0) return new Map<number, number>();
    const counts = new Map<number, number>();
    preparedFiles.forEach((file: PreparedFile) => {
      const fileTagIds = new Set<number>();
      // Only count actual tags from file.tagIds
      file.tagIds?.forEach((id: number) => {
        // Ensure the tagId exists in our merged list of real/category tags before counting
        // This prevents counting stale/invalid tag IDs that might be on a file.
        if (
          mergedTagNameToIdMap.has(mergedFlatTags.find((t) => t.id === id)?.name.toLowerCase() || '') ||
          id === CATEGORY_TAG_ID_FOR_UNTRAINED ||
          (id > 0 && mergedFlatTags.find((t) => t.id === id))
        ) {
          fileTagIds.add(id);
        }
      });
      // Derived tag names are no longer used for counting as getDerivedTagNamesForFile returns []
      // file.derivedTagNames?.forEach((name: string) => { ... });

      fileTagIds.forEach((id) => {
        counts.set(id, (counts.get(id) || 0) + 1);
      });
    });
    return counts;
  }, [preparedFiles, isLoadingMergedTags, mergedTagNameToIdMap, mergedFlatTags]);

  const staticTreeStructure = useMemo(() => {
    if (isLoadingMergedTags || mergedHierarchicalTags.length === 0) return [];
    const initialTagsToKeepIds = new Set<number>();
    initialTagCounts.forEach((count, tagId) => {
      if (count > 0) initialTagsToKeepIds.add(tagId);
    });
    const fullSetToKeep = new Set<number>(initialTagsToKeepIds);
    initialTagsToKeepIds.forEach((id) => {
      if (mergedFlatTags.length > 0) {
        getAllParentTagIds(id, mergedFlatTags).forEach((ancestorId) => fullSetToKeep.add(ancestorId));
      }
    });
    const treeWithInitialCounts = assignCurrentCountsToTree(mergedHierarchicalTags, initialTagCounts);
    const filteredStaticTree = filterTreeByAllowedIds(treeWithInitialCounts, fullSetToKeep);
    return filteredStaticTree;
  }, [mergedHierarchicalTags, mergedFlatTags, initialTagCounts, isLoadingMergedTags]);

  const relevantTagCounts = useMemo(() => {
    const counts = new Map<number, number>();
    finalFilteredAndSortedFiles.forEach((file: PreparedFile) => {
      const fileTagIds = new Set<number>();
      // Only count actual tags from file.tagIds
      file.tagIds?.forEach((id: number) => {
        if (
          mergedTagNameToIdMap.has(mergedFlatTags.find((t) => t.id === id)?.name.toLowerCase() || '') ||
          id === CATEGORY_TAG_ID_FOR_UNTRAINED ||
          (id > 0 && mergedFlatTags.find((t) => t.id === id))
        ) {
          fileTagIds.add(id);
        }
      });
      // Derived tag names no longer contribute to counts
      fileTagIds.forEach((id) => {
        counts.set(id, (counts.get(id) || 0) + 1);
      });
    });
    return counts;
  }, [finalFilteredAndSortedFiles, mergedTagNameToIdMap, mergedFlatTags]);

  const aggregatedRelevantTagCounts = useMemo(() => {
    if (staticTreeStructure.length === 0) return new Map<number, number>();
    return aggregateTagCounts(staticTreeStructure, relevantTagCounts);
  }, [staticTreeStructure, relevantTagCounts]);

  const displayableTags = useMemo(() => {
    if (isLoadingMergedTags) return []; // Return empty or a loading state indicator

    const countUntagged = preparedFiles.filter((file) => !file.tagIds || file.tagIds.length === 0).length;

    const allFilesOption: TagModel = {
      id: ALL_FILES_TAG_ID,
      name: 'All Files',
      active: true, // Assuming active means selectable
      count: preparedFiles.length, // Total files before any tag filtering
      children: [],
    };

    const untaggedOption: TagModel = {
      id: UNTAGGED_FILES_TAG_ID,
      name: 'Untagged',
      active: true,
      count: countUntagged,
      children: [],
    };

    let actualTags: TagModel[] = [];
    if (staticTreeStructure.length > 0) {
      actualTags = assignCurrentCountsToTree(staticTreeStructure, aggregatedRelevantTagCounts);
    }

    // Filter out category root if it has no children with counts, unless it is selected
    // This needs careful consideration based on how ROOT_CATEGORY_TAG_ID is handled in selection
    const finalActualTags = actualTags.filter((tag) => {
      if (tag.id === CATEGORY_TAG_ID_FOR_UNTRAINED) {
        // Keep category root if it has children or if it itself has a count (though direct counts on roots are rare)
        return (tag.children && tag.children.length > 0) || (tag.count && tag.count > 0);
      }
      return true;
    });

    return [allFilesOption, untaggedOption, ...finalActualTags];
  }, [
    isLoadingMergedTags,
    preparedFiles,
    staticTreeStructure,
    aggregatedRelevantTagCounts,
    // CATEGORY_TAG_ID_FOR_UNTRAINED, // Not needed as a dep if it's a constant
  ]);

  return { displayableTags };
};
